import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import { scrollToTop } from "../formHelpers";
import { getBondPrincipalFieldConfig } from "../bondPrincipalFieldConfig";
import { useBondPrincipalForm } from "./useBondPrincipalForm";
import { useBondPrincipalHandlers } from "./useBondPrincipalHandlers";
import { useBondPrincipalNameSuggestion } from "./useBondPrincipalNameSuggestion";
import { useAttorneyAutocompleteSearch } from "../utils/attorneySearch";
import { createBondAttorneySearchErrorHandler } from "../utils/bondAttorneySearchWiring";

/**
 * Shared Redux + dropdown + handlers wiring for bond-creation principal steps (VA / Trustee / Guardianship / Probate).
 * @param {object} opts
 * @param {string} opts.kind — {@link ../bondPrincipalConfig.BOND_PRINCIPAL_KIND} value
 * @param {{ guardians?: unknown[] }} opts.data
 * @param {function} opts.updateData
 * @param {object} opts.globalData
 * @param {boolean} [opts.isGuest]
 * @param {boolean} [opts.includeAttorneyLawFirmIdInPgFetchDeps] — probate parity: PG auto-fetch effect also lists `attorneyLawFirmId`
 */
export function useBondPrincipalScreen({
  kind,
  data = { guardians: [] },
  updateData,
  globalData,
  isGuest,
  includeAttorneyLawFirmIdInPgFetchDeps = false,
}) {
  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const lawfirm = useSelector((state) => state.lawfirm);

  const cfg = useMemo(() => getBondPrincipalFieldConfig(kind), [kind]);

  const attorneyLawFirmId = useMemo(() => {
    if (role === "law_firm_user" || role === "law_firm_admin") {
      return lawfirm.lawfirmId ?? 0;
    }
    return 0;
  }, [role, lawfirm.lawfirmId]);

  const dropdowns = useBondPrincipalForm({
    kind,
    attorneyLawFirmId,
    isGuest: !!isGuest,
  });

  const onAttorneySearchError = useCallback(
    createBondAttorneySearchErrorHandler(toast),
    []
  );

  const attorneySearch = useAttorneyAutocompleteSearch({
    apiClient,
    role,
    lawfirmId: attorneyLawFirmId,
    onSearchError: onAttorneySearchError,
  });

  const [attorneySearchInput, setAttorneySearchInput] = useState("");

  const mergedDropdowns = useMemo(
    () => ({
      ...dropdowns,
      attorneys: attorneySearch.attorneys,
      loadingAttorneys: attorneySearch.loadingAttorneys,
      debouncedSearchAttorneys: attorneySearch.debouncedSearch,
      clearAttorneys: attorneySearch.clearAttorneys,
      attorneySearchInput,
      setAttorneySearchInput,
      attorneySearchEnabled: true,
    }),
    [dropdowns, attorneySearch, attorneySearchInput]
  );

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const handlers = useBondPrincipalHandlers({
    kind,
    data,
    updateData,
    dataRef,
    attorneys: mergedDropdowns.attorneys,
  });

  const nameSuggestion = useBondPrincipalNameSuggestion({
    kind,
    data,
    updateData,
    professionalGuardians: mergedDropdowns.professionalGuardians,
    fetchProfessionalGuardianDetails: handlers.fetchProfessionalGuardianDetails,
  });

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if ((role === "professional guardian" && pgId) || (role === "pg_contact" && pgId)) {
      handlers.fetchProfessionalGuardianDetails(0, pgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- probate includes attorneyLawFirmId (PersonalRepresentatives parity)
  }, [
    role,
    pgId,
    mergedDropdowns.professionalGuardians,
    ...(includeAttorneyLawFirmIdInPgFetchDeps ? [attorneyLawFirmId] : []),
  ]);

  const principalCount =
    Number(globalData?.basicInformation?.[cfg.guardianCountKey] || 1) || 1;

  const guardians = Array.isArray(data.guardians) ? data.guardians : [];

  return {
    cfg,
    dropdowns: mergedDropdowns,
    handlers,
    nameSuggestion,
    principalCount,
    guardians,
  };
}
